import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDashboardStats,
  fetchRecentApplications,
  fetchRecentJobs,
} from '../../redux/slices/dashboardSlice';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import './Dashboard.css';

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, recentApplications, recentJobs, error } = useSelector(
    (state) => state.dashboard
  );

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchRecentApplications());
    dispatch(fetchRecentJobs());
  }, [dispatch]);

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Resumen general</h1>
        <p>Lo más importante de MundoTrabajos en un vistazo.</p>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{stats.users}</div>
            <div className="stat-label">Usuarios</div>
            <div className="stat-desc">+{stats.candidates} profesionales</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.jobs}</div>
            <div className="stat-label">Ofertas activas</div>
            <div className="stat-desc">Publicadas ahora</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.applications}</div>
            <div className="stat-label">Candidaturas</div>
            <div className="stat-desc">Procesos recibidos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.conversations}</div>
            <div className="stat-label">Chats abiertos</div>
            <div className="stat-desc">Conversaciones activas</div>
          </div>
        </div>
      )}

      {stats && stats.trends && (
        <div className="dashboard-charts-grid">
          <div className="dashboard-chart-card no-margin">
            <div className="chart-header">
              <h2>Actividad reciente (Últimos 7 días)</h2>
              <p>Evolución diaria de nuevas ofertas publicadas y candidaturas.</p>
            </div>
            <div className="chart-wrapper">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={stats.trends}
                  margin={{ top: 10, right: 30, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d7192a" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#d7192a" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorApps" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#187d78" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#187d78" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
                  <XAxis dataKey="date" stroke="#666" fontSize={11} tickLine={false} />
                  <YAxis stroke="#666" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)' 
                    }} 
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '13px', paddingTop: '10px' }} />
                  <Area 
                    type="monotone" 
                    name="Nuevas Ofertas" 
                    dataKey="jobs" 
                    stroke="#d7192a" 
                    fillOpacity={1} 
                    fill="url(#colorJobs)" 
                    strokeWidth={2}
                  />
                  <Area 
                    type="monotone" 
                    name="Candidaturas" 
                    dataKey="applications" 
                    stroke="#187d78" 
                    fillOpacity={1} 
                    fill="url(#colorApps)" 
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="dashboard-chart-card no-margin">
            <div className="chart-header">
              <h2>Distribución de Usuarios</h2>
              <p>Proporción entre profesionales y empresas.</p>
            </div>
            <div className="chart-wrapper flex-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Profesionales', value: stats.candidates || 0 },
                      { name: 'Empresas', value: stats.employers || 0 }
                    ]}
                    cx="50%"
                    cy="45%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    <Cell fill="#187d78" />
                    <Cell fill="#d7192a" />
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '8px' 
                    }} 
                  />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '13px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Últimas candidaturas</h2>
            <p>Movimientos recientes de selección.</p>
          </div>
          {recentApplications && recentApplications.length > 0 ? (
            <div className="activity-list">
              {recentApplications.map((app, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-content">
                    <strong>{app.candidate_name}</strong>
                    <small>{app.title}</small>
                  </div>
                  <span className="activity-status">{app.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">No hay candidaturas.</div>
          )}
        </div>

        <div className="dashboard-card">
          <div className="card-header">
            <h2>Ofertas recientes</h2>
            <p>Estado y respuesta obtenida.</p>
          </div>
          {recentJobs && recentJobs.length > 0 ? (
            <div className="activity-list">
              {recentJobs.map((job, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-content">
                    <strong>{job.title}</strong>
                    <small>{job.company}</small>
                  </div>
                  <span className="activity-count">{job.applications_count}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">No hay ofertas.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
